import type {
  CareerSave,
  LeaderboardEntry,
  LeaderboardSnapshot,
  NarrativeMemory,
  PlayerCareerStake,
  SeasonEvent,
  SimulatedHoleEvent,
  TourGolfer,
  TourWorldHistory,
  TournamentForecast,
  TournamentForecastEntry,
  TournamentNarrativeSnapshot,
  TournamentState,
} from './types';

const hash = (text: string) =>
  [...text].reduce((value, char) => Math.imul(value ^ char.charCodeAt(0), 16777619), 2166136261) >>>
  0;
const clamp = (value: number, minimum = 0, maximum = 100) =>
  Math.max(minimum, Math.min(maximum, value));
const formValue = { excellent: 7, good: 3, neutral: 0, poor: -3, veryPoor: -6 } as const;

export const captureLeaderboardSnapshot = (
  state: TournamentState,
  playerHole: number,
): LeaderboardSnapshot => ({
  capturedAtRound: state.currentRound,
  capturedAtPlayerHole: playerHole,
  entries: Object.fromEntries(
    state.leaderboard.map((entry) => [
      entry.golferId,
      {
        position: entry.position,
        totalToPar: entry.total,
        roundToPar: entry.today,
        holesCompleted: entry.currentHole,
      },
    ]),
  ),
});
export const movementSince = (before: LeaderboardSnapshot, entry: LeaderboardEntry) =>
  (before.entries[entry.golferId]?.position ?? entry.position) - entry.position;
export const movementMarker = (movement: number) =>
  movement > 0 ? `↑${movement}` : movement < 0 ? `↓${Math.abs(movement)}` : '—';

export class TournamentForecastEngine {
  generate(
    event: SeasonEvent,
    golfers: readonly TourGolfer[],
    history: TourWorldHistory,
    form: CareerSave['golferForm'],
    season: number,
    playerRating = 68,
  ): TournamentForecast {
    const eventHistory = history.eventHistory[event.id];
    const scored = golfers
      .map((golfer) => {
        const career = history.golferCareerHistory[golfer.id];
        const recentForm = formValue[form[golfer.id] ?? 'neutral'];
        const fit = career.strongestCourses.includes(event.courseId)
          ? 5
          : career.weakestCourses.includes(event.courseId)
            ? -4
            : golfer.preferredEnvironments.some((environment) =>
                  event.courseId.includes(environment),
                )
              ? 2
              : 0;
        const priorWinner = eventHistory.pastChampions.some(
          (result) => result.championId === golfer.id,
        );
        const defending = eventHistory.defendingChampionId === golfer.id;
        const seedVariation = (hash(`${event.id}:${season}:${golfer.id}`) % 7) - 3;
        const score =
          golfer.overallRating * 1.2 +
          (50 - golfer.currentRanking) * 0.25 +
          recentForm +
          fit +
          (priorWinner ? 3 : 0) +
          (defending ? 2 : 0) +
          seedVariation * 0.25;
        const reasons: TournamentForecastEntry['reasons'] = [];
        if (recentForm >= 7) reasons.push('excellent recent form');
        if (priorWinner) reasons.push('prior event winner');
        if (defending) reasons.push('defending champion');
        if (fit > 0) reasons.push('course style fits strengths');
        if (golfer.archetype === 'major-specialist' && event.tier === 'major')
          reasons.push('Major specialist');
        if (golfer.archetype === 'wind-specialist' && event.windRange[1] >= 12)
          reasons.push('elite wind player');
        if (recentForm < 0) reasons.push('currently struggling');
        if (fit < 0) reasons.push('poor course fit');
        if (golfer.archetype === 'veteran') reasons.push('returning veteran');
        if (golfer.archetype === 'rising-prospect') reasons.push('rising prospect');
        return { golfer, score, reasons };
      })
      .sort((a, b) => b.score - a.score);
    const entries = scored.map((item, index): TournamentForecastEntry => ({
      golferId: item.golfer.id,
      forecastRank: index + 1,
      winProbability: Math.round(
        clamp(16 - index * 0.55 + (item.score - scored[0].score) * 0.35, 1, 18),
      ),
      topFiveProbability: Math.round(clamp(48 - index * 1.1, 5, 55)),
      makeCutProbability: event.cutRule.enabled
        ? Math.round(clamp(91 - index * 1.2, 32, 94))
        : undefined,
      reasons: item.reasons.slice(0, 3),
    }));
    const favoriteIds = new Set(entries.slice(0, 5).map((entry) => entry.golferId));
    const darkHorses = entries
      .filter(
        (entry) =>
          entry.forecastRank >= 12 &&
          entry.forecastRank <= 30 &&
          entry.reasons.some(
            (reason) =>
              reason === 'excellent recent form' ||
              reason === 'course style fits strengths' ||
              reason === 'rising prospect',
          ),
      )
      .slice(0, 2);
    const defendingChampion = entries.find(
      (entry) => entry.golferId === eventHistory.defendingChampionId,
    );
    return {
      eventId: event.id,
      generatedAtSeason: season,
      favorites: entries.slice(0, 5),
      contenders: entries.slice(5, 12),
      darkHorses: darkHorses.filter((entry) => !favoriteIds.has(entry.golferId)),
      defendingChampion,
      playerForecast: {
        outlook: playerRating >= scored[10].golfer.overallRating ? 'Competitive' : 'Long Shot',
        reasons: [
          event.cutRule.enabled
            ? 'Four rounds with a cut after Round 2'
            : 'Every qualifier plays all four rounds',
        ],
      },
      fieldStrength: Math.round(
        golfers.reduce((sum, golfer) => sum + golfer.overallRating, 0) / golfers.length,
      ),
      forecastNarratives: defendingChampion
        ? [
            'The defending champion returns.',
            `${event.name} begins a new chapter in its established history.`,
          ]
        : [],
    };
  }
}

export class TournamentNarrativeEngine {
  generate(
    eventId: string,
    state: TournamentState,
    before: LeaderboardSnapshot,
    playerId: string,
    events: SimulatedHoleEvent[],
    memory: NarrativeMemory,
  ): TournamentNarrativeSnapshot {
    const player = state.leaderboard.find((entry) => entry.golferId === playerId)!;
    const movement = movementSince(before, player);
    const oldLeader = Object.entries(before.entries).sort(
      (a, b) => a[1].position - b[1].position,
    )[0]?.[0];
    const leader = state.leaderboard[0];
    const candidates: TournamentNarrativeSnapshot[] = [];
    const make = (
      key: string,
      headline: string,
      priority: number,
      category: TournamentNarrativeSnapshot['category'],
      golferIds: string[],
      facts: TournamentNarrativeSnapshot['supportingFacts'],
    ): TournamentNarrativeSnapshot => ({
      timestamp: { round: state.currentRound, hole: player.currentHole },
      headline,
      priority,
      category,
      golferIds,
      eventId,
      supportingFacts: facts,
      key,
    });
    if (leader.golferId !== oldLeader)
      candidates.push(
        make(
          `leader:${leader.golferId}:${leader.total}`,
          `${leader.golferName} takes the ${state.leaderboard[1]?.total === leader.total ? 'share of the ' : 'outright '}lead at ${formatScore(leader.total)}.`,
          80,
          'leaderboard',
          [leader.golferId],
          [
            { key: 'position', value: leader.position },
            { key: 'score', value: leader.total },
          ],
        ),
      );
    if (movement)
      candidates.push(
        make(
          `player-move:${player.currentHole}:${movement}`,
          `${player.golferName} ${movement > 0 ? `climbs ${movement}` : `falls ${Math.abs(movement)}`} ${Math.abs(movement) === 1 ? 'place' : 'places'} to ${formatPosition(player.position)}.`,
          90,
          'player',
          [playerId],
          [
            { key: 'positionChange', value: movement },
            { key: 'position', value: player.position },
          ],
        ),
      );
    const notable = events.find((event) => event.notable === 'eagle');
    if (notable) {
      const golfer = state.leaderboard.find((entry) => entry.golferId === notable.golferId)!;
      candidates.push(
        make(
          `eagle:${notable.golferId}:${notable.roundNumber}:${notable.holeNumber}`,
          `${golfer.golferName} makes eagle at the ${notable.holeNumber}th.`,
          70,
          'comeback',
          [golfer.golferId],
          [{ key: 'scoreToPar', value: -2 }],
        ),
      );
    }
    if (!candidates.length)
      candidates.push(
        make(
          `steady:${player.currentHole}:${player.total}`,
          `${player.golferName} is ${formatPosition(player.position)} at ${formatScore(player.total)}.`,
          20,
          'leaderboard',
          [playerId],
          [
            { key: 'position', value: player.position },
            { key: 'score', value: player.total },
          ],
        ),
      );
    const chosen =
      candidates
        .sort((a, b) => b.priority - a.priority)
        .find((candidate) => !memory.recentlyShownKeys.includes(candidate.key)) ?? candidates[0];
    memory.recentlyShownKeys = [...memory.recentlyShownKeys, chosen.key].slice(-12);
    memory.lastPrimaryStoryId = chosen.key;
    chosen.golferIds.forEach(
      (id) => (memory.golferMentionCounts[id] = (memory.golferMentionCounts[id] ?? 0) + 1),
    );
    return chosen;
  }
}

export class PlayerCareerStakeEngine {
  generate(career: CareerSave, event: SeasonEvent): PlayerCareerStake[] {
    const stakes: PlayerCareerStake[] = [];
    if (!career.careerStatistics.cutsMade)
      stakes.push({
        id: 'first-cut',
        title: 'Make the first career cut',
        description: 'Reach the weekend for the first time.',
        currentStatus: event.cutRule.enabled ? 'Projected after Round 2' : 'No cut at this event',
        priority: 80,
        resolved: false,
      });
    if (!career.careerStatistics.wins)
      stakes.push({
        id: 'first-win',
        title: 'Chase a first tour victory',
        description: 'A win would establish a place among tour champions.',
        currentStatus: 'Tournament pending',
        priority: 70,
        resolved: false,
      });
    if (event.tier === 'major' && !career.careerStatistics.majorWins)
      stakes.push({
        id: 'first-major',
        title: 'Pursue a first Major',
        description: 'Compete for the tour’s highest event tier.',
        currentStatus: 'Qualified',
        priority: 95,
        resolved: false,
      });
    return stakes;
  }
}
export const formatScore = (score: number) =>
  score === 0 ? 'E' : score > 0 ? `+${score}` : String(score);
export const formatPosition = (position: number) => `T${position}`;
